import { useMemo, useState } from 'react';

import { eventLogsColumns } from '../../../tableColumns/hardwareColumns';
import { IHardwareEvent } from '../../../ts/interface/hardware.interface';
import { PaginationTable } from '../../../components/Redesing/table/pagination-table';
import {
  IColumn,
  ISortConfig,
} from '../../../ts/interface/components/redesing/table.interface';
import CardView from '../../../components/Redesing/card-view';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import IconButtonMenu from '../../../components/Redesing/icon-button-menu';
import { DEFAULT_ROWS_PER_PAGE, EVENT_LOGS_TYPES } from '../../../constants';
import { SeverityPillColor } from '../../../ts/types/settings';
import useUrlState from '../../../hooks/use-url-state';
import { Option, SearchChip } from '../../../ts/interface/components/table.interface';
import {
  MovaticFieldGroupSelectFilter,
  MovaticFieldGroupSingleSelectFilter,
} from '../../../components/Redesing/movatic-field-group';
import { MenuItem } from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import {
  useHardwareEventsRelayQuery,
  EventSortInput,
  EventFilterInput,
} from '../../../hooks/hardware/query/V2/useHardwareEventsRelay';
import { IRental } from '../../../ts/interface/rental.interface';
import { toTitleCase } from '../../../utils';

// Same server-side sort mapping the hardware Event Logs tab uses. Created and
// Type are locked; only Recorded is sortable (see eventLogsColumns).
const SORT_KEY_MAP: Record<string, string> = {
  created_on: 'CREATED_ON',
  recorded_at: 'RECORDED_AT',
  type: 'TYPE',
};

// Options for the Type filter. "Important" is a meta-type that expands to all
// the notable event types (same behavior as the hardware event-logs view).
const eventTypeOptions: Option[] = EVENT_LOGS_TYPES.map((t) => ({
  label: t.name,
  value: t.value,
}));

type HardwareOption = { value: string; label: string };

/**
 * Event Logs for the rental detail page. Mirrors the hardware detail page's
 * Event Logs tab — same GraphQL `listEvents` source (via
 * `useHardwareEventsRelayQuery`), cursor accumulation, filter/chip handling —
 * but adds the two rental-specific behaviors the old REST view had:
 *   - a Hardware selector to switch between the rental's unit and its children
 *   - events fixed to the rental's window: recordedAt is bounded by the
 *     rental's start/end (not user-editable, not in the URL), and the
 *     "Important" event types are the default Type filter
 *
 * This single component backs both the system view and the organization view
 * of the rental detail page (rentalDetail.tsx renders one instance for both).
 */
function RentalHardwareEventLogs({ rental }: { rental: IRental }) {
  const mainHardwareId = rental?.hardware?.id;

  const mainHardwareLabel = `${toTitleCase(rental?.hardware?.type_name)} ${toTitleCase(
    rental?.hardware?.hardware_product_name ?? ''
  )} ${mainHardwareId ?? ''}`.trim();

  // Selectable hardware: the rental's children plus the rental's own unit.
  const hardwareOptions: HardwareOption[] = useMemo(
    () => [
      ...(rental?.hardware?.children?.map((child) => ({
        value: child.id,
        label: `${toTitleCase(child?.type_name)} ${toTitleCase(child?.display_name)} ${
          child.id
        }`.trim(),
      })) || []),
      {
        value: mainHardwareId ?? '',
        label: mainHardwareLabel,
      },
    ],
    [rental?.hardware?.children, mainHardwareId, mainHardwareLabel]
  );

  const [exportData, setExportData] = useState<boolean>(false);
  const [events, setEvents] = useState<IHardwareEvent[]>([]);

  const [afterCursor, setAfterCursor] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(0);

  // The event window is pinned to the rental itself (end is empty for an
  // in-progress rental). It is not a user filter, so it never enters URL state.
  const rentalStart = rental?.start?.time ?? '';
  const rentalEnd = rental?.end?.time ?? '';

  const [filters, setFilters, isComplete, isRestoring] = useUrlState({
    sizePerPage: DEFAULT_ROWS_PER_PAGE,
    search: '',
    hardwareId: mainHardwareId,
    types: 'Important',
    sortConfig: {
      key: 'recorded_at',
      direction: 'desc',
    } as ISortConfig,
  });

  // Fall back to the live rental prop so the query never gets stuck disabled if
  // the persisted hardwareId is ever missing.
  const activeHardwareId = filters.hardwareId || mainHardwareId;

  const [chips, setChips] = useState<SearchChip[]>([]);

  // Pending (modal) selections, applied on "Apply".
  const [selectedHardwareId, setSelectedHardwareId] = useState<string | undefined>(
    mainHardwareId
  );
  const [selectedTypes, setSelectedTypes] = useState<Option[]>([]);

  // Sync applied Type chips + the pending selection from the (URL-persisted)
  // applied `types` value, so the chip and query stay in lock-step on refresh.
  const syncTypeChips = (values: string[]) => {
    setChips((prev) => {
      const withoutTypes = prev.filter((chip) => chip.field !== 'types');
      const typeChips: SearchChip[] = values.map((value) => ({
        label: 'Type',
        field: 'types',
        value,
        displayValue: eventTypeOptions.find((o) => o.value === value)?.label ?? value,
      }));
      return [...withoutTypes, ...typeChips];
    });
  };

  // Show a Hardware chip only when viewing a child unit; the rental's own unit
  // is the default and needs no chip.
  const syncHardwareChip = (hardwareId?: string) => {
    setChips((prev) => {
      const withoutHardware = prev.filter((chip) => chip.field !== 'hardwareId');
      if (!hardwareId || hardwareId === mainHardwareId) return withoutHardware;
      const option = hardwareOptions.find((o) => o.value === hardwareId);
      if (!option) return withoutHardware;
      return [
        ...withoutHardware,
        {
          label: 'Hardware',
          field: 'hardwareId',
          value: hardwareId,
          displayValue: option.label,
        },
      ];
    });
  };

  const sort = useMemo<EventSortInput[]>(() => {
    const field = SORT_KEY_MAP[filters.sortConfig.key] || 'RECORDED_AT';
    const order = filters.sortConfig.direction === 'asc' ? 'ASC' : 'DESC';
    return [{ field, order } as EventSortInput];
  }, [filters.sortConfig]);

  const filter = useMemo<EventFilterInput | undefined>(() => {
    const conditions: EventFilterInput[] = [];

    // Events are scoped to when they happened during the rental, so bound
    // recordedAt (not createdOn) by the rental's fixed window.
    if (rentalStart) {
      conditions.push({
        recordedAt: { greaterThanOrEqual: rentalStart },
      });
    }
    if (rentalEnd) {
      conditions.push({
        recordedAt: { lessThanOrEqual: rentalEnd },
      });
    }
    if (filters.search) {
      conditions.push({
        type: { ilike: `%${filters.search}%` },
      });
    }
    if (filters.types) {
      const selected = filters.types.split(',').filter(Boolean);
      // "Important" expands to every notable event type.
      const expanded = selected.includes('Important')
        ? EVENT_LOGS_TYPES.filter((t) => t.value !== 'Important').map((t) => t.value)
        : selected;
      if (expanded.length > 0) {
        conditions.push({ type: { in: expanded } });
      }
    }

    if (conditions.length === 0) return undefined;
    if (conditions.length === 1) return conditions[0];
    return { and: conditions };
  }, [rentalStart, rentalEnd, filters.search, filters.types]);

  const {
    data: eventsData,
    hasNextPage,
    endCursor,
    isLoading,
  } = useHardwareEventsRelayQuery(activeHardwareId, {
    enabled: !!activeHardwareId && isComplete,
    first: filters.sizePerPage,
    after: afterCursor,
    sort,
    filter,
  });

  // Map GraphQL events and accumulate across pages.
  useMemo(() => {
    if (!eventsData.length && !afterCursor) {
      setEvents([]);
      return;
    }
    const mapped = eventsData.map((event) => {
      const colorType = EVENT_LOGS_TYPES.find(({ value }) => value === event.type);
      return {
        id: event.id,
        created_on: event.createdOn,
        recorded_at: event.recordedAt,
        type: event.type ?? '',
        color_type: (colorType?.color as SeverityPillColor) ?? null,
      };
    });
    if (afterCursor) {
      setEvents((prev) => [...prev, ...mapped]);
    } else {
      setEvents(mapped);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsData]);

  // Restore hardware/type chips (and the pending selections) from URL
  // state on initial load, so a refresh shows the filters that are applied.
  useMemo(() => {
    if (isComplete) {
      syncHardwareChip(filters.hardwareId);
      setSelectedHardwareId(filters.hardwareId ?? mainHardwareId);
      if (filters.types) {
        const values = filters.types.split(',').filter(Boolean);
        syncTypeChips(values);
        setSelectedTypes(
          values.map((value) => ({
            label: eventTypeOptions.find((o) => o.value === value)?.label ?? value,
            value,
          }))
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete]);

  const resetPagination = () => {
    setAfterCursor(null);
    setPageNumber(0);
  };

  const handleChipDelete = (deletedGroup: SearchChip) => {
    if (deletedGroup.label === 'Hardware') {
      resetPagination();
      setEvents([]);
      setFilters((prev) => ({ ...prev, hardwareId: mainHardwareId }));
      setSelectedHardwareId(mainHardwareId);
      setChips((prevChips) => prevChips.filter((chip) => chip.field !== 'hardwareId'));
    }
    if (deletedGroup.label === 'Type') {
      resetPagination();
      setEvents([]);
      setFilters((prev) => ({
        ...prev,
        types: '',
      }));
      setChips((prevChips) => prevChips.filter((chip) => chip.field !== 'types'));
      setSelectedTypes([]);
    }
  };

  return (
    <CardView
      headerActions={
        <IconButtonMenu
          buttons={[
            {
              label: 'Export',
              onClick: () => setExportData(true),
              startIcon: <FileDownloadIcon />,
            },
          ]}
        />
      }
      content={
        <PaginationTable
          dataId={'rental-hardware-event-logs'}
          showFilter
          customKey={'id'}
          sortDirection={'desc'}
          exportData={exportData}
          csvFileName={'rental_event_logs.csv'}
          onExport={() => setExportData(false)}
          sortBy={filters.sortConfig.key}
          chips={chips}
          handleChipDelete={handleChipDelete}
          hasMore={hasNextPage}
          onApplyFilter={() => {
            resetPagination();
            setEvents([]);
            if (selectedHardwareId && selectedHardwareId !== filters.hardwareId) {
              setFilters((prev) => ({ ...prev, hardwareId: selectedHardwareId }));
              syncHardwareChip(selectedHardwareId);
            }
            const typeValues = selectedTypes.map((t) => t.value.toString());
            setFilters((prev) => ({ ...prev, types: typeValues.join(',') }));
            syncTypeChips(typeValues);
          }}
          spaceFilters={
            <>
              <MovaticFieldGroupSingleSelectFilter
                label={'Hardware'}
                value={selectedHardwareId}
                onChange={(e: SelectChangeEvent) => setSelectedHardwareId(e.target.value)}
                options={hardwareOptions.map((option, index) => (
                  <MenuItem key={index} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              />
              <MovaticFieldGroupSelectFilter
                dataId="rental-event-logs-type"
                label="Type"
                value={selectedTypes}
                onChange={(event: React.SyntheticEvent, newValue: any[]) =>
                  setSelectedTypes(newValue)
                }
                options={eventTypeOptions}
              />
            </>
          }
          onSortChange={(sort) => {
            setEvents([]);
            resetPagination();
            setFilters({
              ...filters,
              sortConfig: {
                key: sort.key,
                direction: sort.direction as 'desc' | 'asc',
              },
            });
          }}
          handleSearch={(event) => {
            setEvents([]);
            resetPagination();
            setFilters({
              ...filters,
              search: event.target.value,
            });
          }}
          searchText={filters.search}
          isLoading={isLoading || !activeHardwareId || !isComplete || isRestoring}
          columns={eventLogsColumns as IColumn[]}
          items={events || []}
          onPageChange={(page, newPage) => {
            const pageNumberAux = newPage + 1;
            if (events?.length && events.length / filters.sizePerPage < pageNumberAux) {
              setAfterCursor(endCursor);
            }
            setPageNumber(newPage);
          }}
          onRowsPerPageChange={(pageSizeString) => {
            const pageSize = Number(pageSizeString.target.value);
            setEvents([]);
            resetPagination();
            setFilters({
              ...filters,
              sizePerPage: pageSize,
            });
          }}
          page={pageNumber}
          rowsPerPage={filters.sizePerPage}
          searchPlaceholder={'Search Event Logs'}
          noDataText={'No event logs for this rental'}
          localSearch={false}
        />
      }
      title={'Event Logs'}
    />
  );
}

export default RentalHardwareEventLogs;
